// traceForgeOps.js — what the scan window's agents did on the CODE FORGE
// (Forgejo / GitHub: PRs, issues, CI runs, branch pushes), reconstructed from
// raw request bodies (the same hydration mechanic as the Files/Linear/Research
// X-rays: tool_use/tool_result blocks per prompt, deduped across each
// session's cumulative re-sends, aggregated per target and actor).
//
// The twist vs the siblings: forge work does NOT arrive as a dedicated tool.
// Measured on live traffic, GitHub-via-MCP is zero — the entire PR/issue/CI
// lifecycle flows through the SHELL, in five recurring channels:
//
//   curl    — direct forge API calls ("$FJ/repos/o/r/pulls/123", api.github.com)
//   gh      — the GitHub CLI (pr view/create/comment, run view, api …)
//   script  — scripts/forgejo-pr.py (view/create/comment/… verbs)
//   tea     — the Gitea/Forgejo CLI (tea pr 79 …)
//   git     — `git push` (the one git verb the forge can see)
//
// So this module is a COMMAND parser, not a tool-name matcher: each Bash-family
// tool_use has its command split into quote-aware statements (heredoc bodies
// lifted out first — PR descriptions ride inside them, and a heredoc that
// merely MENTIONS "gh" or a PR number must never classify as an op), and each
// statement is matched against the channel grammars above. Conservative on
// purpose: a statement that names the forge without an API path (keychain
// lookups, greps over forgejo-pr.py itself) produces nothing.
//
// Like the Linear X-ray, ops capture the substantive CONTENT they moved — a
// comment body posted through --body or -d '{"body":…}', a PR description from
// a create's heredoc, a title PATCH — so the tab can replay what was written,
// not just that a write happened.

// Bash-family tool names whose input.command carries shell text. PowerShell is
// deliberately included — gh/git grammar is shell-agnostic enough to match.
// Exported as the ONE shell-family name matcher (traceSkillRuns' channel
// fingerprints and the X-ray substrate windows key on the same set).
export const SHELL_TOOL_RE = /^(bash|bash_tool|powershell|shell|run_terminal_cmd|execute_command)$/i;

// Per-op captured content (comment/description bodies). Forge prose, same cap
// rationale as Linear's.
const CONTENT_CAP = 2000;
// Bound per-command op fan-out — a `for i in $(seq 1 30)` curl loop is ONE
// intent; distinct-resource dedup below already collapses it, this is the
// pathological backstop.
const OPS_PER_COMMAND_CAP = 8;
// Bound title gleaning over a huge list result.
const GLEAN_CAP = 40;

function str(v) {
  return typeof v === "string" && v ? v : null;
}

function cap(s) {
  const t = typeof s === "string" ? s : "";
  return t.length > CONTENT_CAP ? t.slice(0, CONTENT_CAP) : t;
}

function capInfo(s) {
  const t = typeof s === "string" ? s : "";
  return { text: t.length > CONTENT_CAP ? t.slice(0, CONTENT_CAP) : t, truncated: t.length > CONTENT_CAP };
}

// djb2 — stable synthetic dedup key (same as the sibling libs).
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// Is this tool_use a shell command we should parse? Accepts the bare names and
// the MCP-wrapped ones (mcp__workspace__bash).
export function shellCommandOf(block) {
  if (block?.type !== "tool_use") return null;
  const name = String(block.name || "");
  const last = name.split("__").pop();
  if (!SHELL_TOOL_RE.test(last)) return null;
  const input = block.input && typeof block.input === "object" ? block.input : {};
  return str(input.command) || str(input.cmd) || str(input.script) || null;
}

// ── Shell text prep ──────────────────────────────────────────────────────────

// Lift heredoc bodies out of the command before any grammar runs. Returns the
// command with each body replaced by a numbered placeholder token plus the
// bodies themselves — op parsing works on the stripped text (so heredoc
// CONTENT can't produce phantom ops) and content capture resolves the
// placeholder back to the body (PR descriptions ride in <<'EOF' blocks).
export function stripHeredocs(cmd) {
  const bodies = [];
  const src = String(cmd || "").replace(/\\\r?\n/g, " "); // join line continuations
  const open = /<<-?\s*(["']?)([A-Za-z_][\w]*)\1[^\n]*\n/g;
  let out = "";
  let last = 0;
  let m;
  while ((m = open.exec(src))) {
    const term = m[2];
    const bodyStart = open.lastIndex;
    const endRe = new RegExp(`^\\s*${term}\\s*$`, "m");
    const rest = src.slice(bodyStart);
    const em = endRe.exec(rest);
    const bodyEnd = em ? bodyStart + em.index : src.length;
    out += src.slice(last, m.index) + ` HD${bodies.length} `;
    bodies.push(src.slice(bodyStart, bodyEnd).replace(/\r?\n$/, ""));
    last = em ? bodyEnd + em[0].length : src.length;
    open.lastIndex = last;
  }
  out += src.slice(last);
  return { text: out, bodies };
}

// Quote-aware statement split: `;`, `&&`, `||`, `|`, newline — but never
// inside '…' or "…" (a -d '{"a":"b|c"}' payload must stay whole). Subshell
// parens are NOT tracked; command-substitution content stays in its statement,
// which is what the grammars want ($(cat <<HD0) resolves as content).
export function splitStatements(text) {
  const parts = [];
  let curStart = 0;
  let q = null;
  const s = String(text || "");
  const push = (end) => {
    const t = s.slice(curStart, end).trim();
    if (t) parts.push(t);
  };
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === "\\" && q === '"') i += 1;
      else if (c === q) q = null;
      continue;
    }
    if (c === "'" || c === '"') { q = c; continue; }
    if (c === "\\") { i += 1; continue; }
    if (c === "\n" || c === ";" || c === "|" || (c === "&" && s[i + 1] === "&")) {
      push(i);
      if ((c === "&" && s[i + 1] === "&") || (c === "|" && s[i + 1] === "|")) i += 1;
      curStart = i + 1;
    }
  }
  push(s.length);
  return parts;
}

// The statement with its quoted spans blanked (length-preserving). Trigger
// detection runs on THIS text, so a --body/-d payload that merely mentions
// "git push" or a curl URL can never classify as a real op. Argument
// extraction keeps the original statement — URLs and bodies live inside the
// quotes on purpose.
export function unquoted(stmt) {
  const s = String(stmt || "");
  let out = "";
  let q = null;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === "\\" && q === '"') { out += "  "; i += 1; continue; }
      if (c === q) { q = null; out += " "; continue; }
      out += " ";
      continue;
    }
    if (c === "'" || c === '"') { q = c; out += " "; continue; }
    out += c;
  }
  return out;
}

// The value of a `--flag value` / `--flag=value` occurrence, unquoting and
// resolving heredoc placeholders. Used for --body/--title/-d payloads.
function flagValue(stmt, bodies, ...flags) {
  for (const f of flags) {
    const re = new RegExp(`${f}(?:=|\\s+)("((?:[^"\\\\]|\\\\.)*)"|'([^']*)'|(\\S+))`);
    const m = re.exec(stmt);
    if (!m) continue;
    let v = m[3] ?? m[4] ?? m[2] ?? m[1] ?? "";
    if (m[2] !== undefined) v = m[2].replace(/\\(.)/g, "$1");
    const hd = /(?:^|\s)HD(\d+)(?:\s|$)/.exec(v);
    if (hd) v = bodies[Number(hd[1])] ?? v;
    // `--body "$(cat <<EOF…)"` — the placeholder sits inside a $() wrapper.
    const hd2 = /(?:^|\s)HD(\d+)(?:\s|$)/.exec(m[0]);
    if (!hd && hd2) v = bodies[Number(hd2[1])] ?? v;
    return v.trim() || null;
  }
  return null;
}

// ── The URL grammar ──────────────────────────────────────────────────────────

// Resource segment → kind. `pulls`/`issues` become numbered TARGETS; the rest
// group at repo level.
const RES_OF = {
  pulls: "pr", pull: "pr", issues: "issue", actions: "ci", "check-runs": "ci",
  statuses: "ci", status: "ci", commits: "commit", releases: "release",
  branches: "branch", contents: "contents", raw: "contents", labels: "meta",
  milestones: "meta", collaborators: "meta", reviews: "meta",
};

// Parse one URL-ish candidate into forge coordinates. Handles three shapes:
//   https://host/api/v1/repos/o/r/pulls/123/comments   (Forgejo)
//   https://api.github.com/repos/o/r/issues/9          (GitHub)
//   $FJ/repos/o/r/actions/tasks · $API/pulls/386.diff  (env-var API bases)
// A candidate with no recognizable forge path returns null — bare hostnames
// (keychain lookups, secret ids) must not become ops.
export function parseForgePath(candidate) {
  const s = String(candidate || "");
  let path = null;
  const api = /\/api\/v1\/([^\s"'?#]+)/.exec(s);
  if (api) path = api[1];
  else if (/^https?:\/\/api\.github\.com\//.test(s)) path = s.replace(/^https?:\/\/api\.github\.com\//, "").split(/[?#]/)[0];
  else {
    const r = /(?:^|\/)(repos\/[\w.-]+\/[\w.-]+\/[^\s"'?#]+)/.exec(s);
    if (r) path = r[1];
    else {
      // $VAR/pulls/123 — repo rode in the variable; keep the resource.
      const v = /\$\{?[A-Za-z_]\w*\}?\/((?:pulls|issues|actions|commits|releases)(?:\/[^\s"'?#]*)?)/.exec(s);
      if (v) path = v[1];
    }
  }
  if (!path) return null;
  // Dynamic shell fragments inside the path ($(git rev-parse…), ${SHA}) are
  // values we can't resolve — collapse each to "*" so labels stay clean.
  const segs = path.replace(/^\//, "").split("/").filter(Boolean)
    .map((seg) => (/[$({]/.test(seg) ? "*" : seg));
  let repo = null;
  if (segs[0] === "repos" && segs.length >= 3) {
    repo = `${segs[1]}/${segs[2]}`;
    segs.splice(0, 3);
  }
  if (!segs.length) return repo ? { repo, res: "repo", num: null, sub: null, endpoint: path } : null;
  const res = RES_OF[segs[0]] || "other";
  let num = null;
  let sub = null;
  if (segs[1]) {
    const nm = /^(\d+)(?:\.(?:diff|patch))?$/.exec(segs[1]);
    if (nm) num = Number(nm[1]);
    else sub = segs[1];
  }
  if (segs[2]) sub = segs[2];
  // commits/<sha>/status is CI observation, not a commit read.
  if (res === "commit" && (sub === "status" || sub === "statuses" || sub === "check-runs")) {
    return { repo, res: "ci", num: null, sub, endpoint: path };
  }
  // actions/runs/<id>[/jobs|/logs] — the run id is the subject, and "which
  // run" is the difference between a specific CI read and a vague browse
  // (the reading study's judge rejected exactly that vagueness).
  if (res === "ci" && num == null) {
    const rm = /(?:^|\/)runs\/(\d+)/.exec(path);
    if (rm) num = Number(rm[1]);
  }
  return { repo, res, num, sub, endpoint: path };
}

// Repo context declared in the COMMAND (not the URL): --repo/-R flags and the
// GITHUB_REPOSITORY export that forgejo-pr.py reads.
export function repoContextOf(text) {
  const m =
    /(?:--repo|-R)[=\s]+["']?([\w.-]+\/[\w.-]+)/.exec(text) ||
    /GITHUB_REPOSITORY\s*=\s*["']?([\w.-]+\/[\w.-]+)/.exec(text);
  return m ? m[1] : null;
}

// ── Per-channel statement grammars ───────────────────────────────────────────

const WRITE_METHOD_RE = /-X\s*["']?(POST|PUT|PATCH|DELETE)\b/i;
const DATA_FLAG_RE = /(^|\s)(-d|--data|--data-raw|--data-binary|--data-urlencode|--json|-F)\b/;
const URL_CANDIDATE_RE = /(?:https?:\/\/[^\s"'<>)\]]+|\$\{?[A-Za-z_]\w*\}?\/[^\s"'<>)\]]+)/g;

// A `-d '<json>'` payload's substantive fields. Comment/issue bodies ride in
// `body`; PATCHes carry title/state.
function jsonPayloadFields(stmt, bodies) {
  const raw = flagValue(stmt, bodies, "--data-raw", "--data-binary", "--data", "-d", "--json");
  if (!raw || raw[0] !== "{") return { content: null, fields: {} };
  try {
    const d = JSON.parse(raw);
    const fields = {};
    for (const k of ["title", "state", "base", "head", "assignee", "ref"]) {
      if (typeof d[k] === "string" && d[k]) fields[k] = d[k];
    }
    return { content: str(d.body) || null, fields };
  } catch {
    return { content: null, fields: {} };
  }
}

function curlOps(stmt, bare, bodies, repoCtx) {
  if (!/(^|\s)curl\s/.test(bare)) return [];
  const method = WRITE_METHOD_RE.exec(bare)?.[1]?.toUpperCase()
    || (DATA_FLAG_RE.test(bare) ? "POST" : "GET");
  const write = method !== "GET" && method !== "HEAD";
  const actAs = /-H\s+["']?Sudo:\s*([\w.-]+)/i.exec(stmt)?.[1] || null;
  const { content, fields } = write ? jsonPayloadFields(stmt, bodies) : { content: null, fields: {} };
  const ops = [];
  const seen = new Set();
  // Collapse command substitutions before URL scanning — the space inside
  // `commits/$(git rev-parse HEAD)/status` would otherwise cut the candidate
  // mid-path. parseForgePath maps the marker to "*" like any dynamic segment.
  const scanText = stmt.replace(/\$\([^)]*\)/g, "$__DYN__");
  for (const cand of scanText.match(URL_CANDIDATE_RE) || []) {
    const hit = parseForgePath(cand);
    if (!hit) continue;
    const idKey = `${hit.repo || ""}|${hit.res}|${hit.num || ""}|${hit.sub || ""}`;
    if (seen.has(idKey) || ops.length >= OPS_PER_COMMAND_CAP) continue;
    seen.add(idKey);
    const c = capInfo(content || "");
    ops.push({
      channel: "curl",
      action: `${method} ${hit.res}${hit.sub ? `/${hit.sub}` : ""}`,
      kind: write ? "write" : hit.num ? "read" : "browse",
      res: hit.res, num: hit.num, repo: hit.repo || repoCtx,
      create: write && method === "POST" && !hit.num && (hit.res === "pr" || hit.res === "issue"),
      content: c.text || null, contentTruncated: c.truncated,
      fields: { ...fields, ...(actAs ? { as: actAs } : {}) },
      endpoint: hit.endpoint,
    });
  }
  return ops;
}

const GH_READ_VERBS = new Set(["view", "diff", "checks", "list", "status", "download", "watch"]);
const GH_WRITE_VERBS = new Set(["create", "comment", "merge", "close", "reopen", "edit", "review", "ready", "cancel", "rerun", "run", "dispatch", "lock", "unlock"]);

function ghOps(stmt, bare, bodies, repoCtx) {
  const m = /(^|\s)gh\s+(pr|issue|run|workflow|api|release)\s+(\S+)/.exec(bare);
  if (!m) return [];
  const [, , group, rawVerb] = m;
  const repo = repoContextOf(stmt) || repoCtx;
  if (group === "api") {
    // gh api <path> [-X method] — same coordinates as curl. The path is an
    // ARGUMENT (usually quoted), so it extracts from the original statement.
    const pm = /gh\s+api\s+["']?([^\s"']+)/.exec(stmt);
    const hit = pm ? parseForgePath(pm[1]) : null;
    if (!hit) return [];
    const method = WRITE_METHOD_RE.exec(bare)?.[1]?.toUpperCase() || (/-f\s|\s--field\s/.test(bare) ? "POST" : "GET");
    const write = method !== "GET";
    return [{
      channel: "gh",
      action: `api ${method} ${hit.res}${hit.sub ? `/${hit.sub}` : ""}`,
      kind: write ? "write" : hit.num ? "read" : "browse",
      res: hit.res, num: hit.num, repo: hit.repo || repo,
      create: false, content: null, contentTruncated: false, fields: {}, endpoint: hit.endpoint,
    }];
  }
  const verb = rawVerb.replace(/^--?/, "");
  const isWrite = GH_WRITE_VERBS.has(verb) && !(group === "run" && verb === "view");
  const isRead = GH_READ_VERBS.has(verb);
  if (!isWrite && !isRead) return [];
  const num = (() => {
    const nm = new RegExp(`gh\\s+${group}\\s+${rawVerb}\\s+(?:--\\S+\\s+)*["']?#?(\\d{1,7})\\b`).exec(bare);
    return nm ? Number(nm[1]) : null;
  })();
  const res = group === "pr" ? "pr" : group === "issue" ? "issue" : group === "release" ? "release" : "ci";
  const create = verb === "create" && (res === "pr" || res === "issue");
  // A --log/--log-failed run view is a LOG read — surface it in the action so
  // the observation pass knows this result is a log body to keep.
  const logRead = res === "ci" && /--log(-failed)?\b/.test(bare);
  const title = flagValue(stmt, bodies, "--title", "-t");
  const body = flagValue(stmt, bodies, "--body", "-b");
  // --body-file / -F points at a path — that's a reference, not the body.
  const bodyFile = flagValue(stmt, bodies, "--body-file", "-F");
  const c = capInfo(body || "");
  return [{
    channel: "gh",
    action: `${group} ${verb}${logRead ? " log" : ""}`,
    kind: isWrite ? "write" : num ? "read" : "browse",
    res, num, repo,
    create,
    title: title || null,
    content: c.text || null, contentTruncated: c.truncated,
    fields: bodyFile ? { bodyFile } : {}, endpoint: null,
  }];
}

// The full verb set of scripts/forgejo-pr.py — review-from-digest and
// commit-pr are the review bot's own submission/push verbs; dropping them
// would blind this X-ray to exactly the automation it exists to show.
const SCRIPT_READ_VERBS = new Set(["view", "comments", "diff", "files", "list", "search", "exists", "checks", "status", "reviews", "reads"]);
const SCRIPT_WRITE_VERBS = new Set(["create", "comment", "edit", "review", "review-from-digest", "push", "commit-pr", "merge", "close", "reopen", "label"]);

function scriptOps(stmt, bare, bodies, repoCtx) {
  // Invocation only: the script name followed by a known verb. A grep over the
  // script, or `--help`, is not an op.
  const m = /forgejo-pr\.py["']?\s+(?:--repo[=\s]+["']?([\w.-]+\/[\w.-]+)["']?\s+)?([a-z-]+)\b/.exec(bare);
  if (!m) return [];
  const [, repoFlag, verb] = m;
  const isWrite = SCRIPT_WRITE_VERBS.has(verb);
  if (!isWrite && !SCRIPT_READ_VERBS.has(verb)) return [];
  const repo = repoFlag || repoContextOf(stmt) || repoCtx;
  const nm = new RegExp(`${verb}\\s+(?:--\\S+(?:[=\\s]+\\S+)?\\s+)*["']?#?(\\d{1,7})\\b`).exec(bare);
  const num = nm ? Number(nm[1]) : null;
  const title = flagValue(stmt, bodies, "--title");
  const body = flagValue(stmt, bodies, "--body", "--message", "-m");
  const c = capInfo(body || "");
  return [{
    channel: "script",
    action: verb,
    kind: isWrite ? "write" : num ? "read" : "browse",
    res: "pr", num, repo,
    create: verb === "create",
    title: title || null,
    content: c.text || null, contentTruncated: c.truncated,
    fields: {}, endpoint: null,
  }];
}

function teaOps(stmt, bare, bodies, repoCtx) {
  const repoEarly = repoContextOf(stmt) || repoCtx;
  // tea api <path> — same coordinates as curl/gh api. Trigger on the bare
  // text; the path argument extracts from the original (it's usually quoted).
  if (/(^|[\s;&|(])tea\s+api\s/.test(bare)) {
    const api = /(^|[\s;&|(])tea\s+api\s+["']?([^\s"']+)/.exec(stmt);
    if (!api) return [];
    const hit = parseForgePath(api[2]);
    if (!hit) return [];
    return [{
      channel: "tea",
      action: `api ${hit.res}${hit.sub ? `/${hit.sub}` : ""}`,
      kind: hit.num ? "read" : "browse",
      res: hit.res, num: hit.num, repo: hit.repo || repoEarly,
      create: false, content: null, contentTruncated: false, fields: {}, endpoint: hit.endpoint,
    }];
  }
  // tea actions runs list … — CI browsing.
  if (/(^|[\s;&|(])tea\s+actions?\b/.test(bare)) {
    return [{
      channel: "tea", action: "actions list", kind: "browse",
      res: "ci", num: null, repo: repoEarly,
      create: false, content: null, contentTruncated: false, fields: {}, endpoint: null,
    }];
  }
  const m = /(^|[\s;&|(])tea\s+(prs?|pulls|issues?|comment)\b/.exec(bare);
  if (!m) return [];
  const group = m[2];
  const repo = repoEarly;
  const nm = /\s#?(\d{1,7})(\s|$)/.exec(bare.slice(m.index));
  const num = nm ? Number(nm[1]) : null;
  const write = group === "comment";
  const body = write ? flagValue(stmt, bodies, "--body", "-b") : null;
  const c = capInfo(body || "");
  return [{
    channel: "tea",
    action: write ? "comment" : `${/issue/.test(group) ? "issue" : "pr"} view`,
    kind: write ? "write" : num ? "read" : "browse",
    res: /issue/.test(group) ? "issue" : "pr", num, repo,
    create: false,
    content: c.text || null, contentTruncated: c.truncated,
    fields: {}, endpoint: null,
  }];
}

function gitPushOps(bare) {
  // git may be invoked by full path (/opt/homebrew/bin/git push …). Matches
  // only the bare (unquoted) text — a commit message SAYING "git push" is not
  // a push.
  const m = /(^|[\s;&|(])(?:\S*\/)?git\s+(?:-C\s+\S+\s+)?push\b(.*)$/.exec(bare);
  if (!m) return [];
  // `git push [flags] [remote [refspec]]` — first two non-flag tokens.
  const args = (m[2] || "").trim().split(/\s+/).filter(Boolean);
  const pos = args.filter((a) => a && !a.startsWith("-") && !/^\d*>&?\d*$/.test(a));
  const remote = pos[0] || null;
  const ref = pos[1] || null;
  const fields = {};
  if (remote) fields.remote = remote;
  if (ref) fields.ref = ref.replace(/^\+/, "");
  if (/--force(?:-with-lease)?|(^|\s)-f\b/.test(m[2] || "")) fields.force = "yes";
  return [{
    channel: "git",
    action: "push",
    kind: "push",
    res: "branch", num: null, repo: null,
    create: false, content: null, contentTruncated: false,
    fields, endpoint: null,
  }];
}

// Inline python (heredoc scripts, -c one-liners) driving the forge API — a
// real channel on live traffic: `python3 <<'EOF' … urllib.request …
// BASE=".../api/v1" … get(f"pulls/{n}") …`. We don't parse python; we scan the
// script text the statement carries for forge coordinates, coarsely. Only
// fires for python-runner statements — a `cat > notes.json` heredoc that
// mentions API URLs is content, not a call, and stays invisible.
const PY_RUNNER_RE = /(^|[\s;&|(])(?:\S*\/)?(python3?(?:\.\d+)?|uv|node|deno)\b/;
const PY_WRITE_RE = /\.(post|patch|put|delete)\s*\(|method\s*=\s*["'](POST|PATCH|PUT|DELETE)|Request\([^)]*data\s*=/i;
const PY_RES_FRAGMENT_RE = /["'{(/f](pulls|issues|actions|commits|releases)(?:\/(\d+))?\b/g;

function pythonApiOps(stmt, bare, bodies, repoCtx) {
  if (!PY_RUNNER_RE.test(bare)) return [];
  if (/forgejo-pr\.py/.test(bare) || /(^|\s)curl\s/.test(bare)) return []; // owned by other grammars
  let hay = stmt;
  for (const hm of stmt.matchAll(/(?:^|\s)HD(\d+)(?:\s|$)/g)) hay += `\n${bodies[Number(hm[1])] || ""}`;
  if (!/https?:\/\/[^\s"']+\/api\/v1|api\.github\.com/.test(hay)) return [];
  const write = PY_WRITE_RE.test(hay);
  const repo = /REPO\w*\s*=\s*["']([\w.-]+\/[\w.-]+)/.exec(hay)?.[1] || repoContextOf(hay) || repoCtx;
  const ops = [];
  const push = (res, num, endpoint) => {
    if (ops.some((o) => o.res === res && o.num === num)) return;
    ops.push({
      channel: "python",
      action: `api script ${res}`,
      kind: write ? "write" : num ? "read" : "browse",
      res, num, repo,
      create: false, content: null, contentTruncated: false, fields: {}, endpoint,
    });
  };
  for (const cand of hay.match(URL_CANDIDATE_RE) || []) {
    const hit = parseForgePath(cand);
    if (hit && hit.res !== "repo") push(hit.res, hit.num, hit.endpoint);
  }
  if (!ops.length) {
    // No full URL resolved — the base rode a variable and the resource lives
    // in an f-string fragment (get(f"pulls/{n}")).
    let m;
    PY_RES_FRAGMENT_RE.lastIndex = 0;
    while ((m = PY_RES_FRAGMENT_RE.exec(hay))) {
      push(RES_OF[m[1]] || "other", m[2] ? Number(m[2]) : null, null);
      if (ops.length >= 3) break; // coarse channel — keep the fan-out tiny
    }
  }
  return ops;
}

// One shell command → its forge ops (no result pairing yet — that happens in
// extractForgeOps where the block's result is known).
export function classifyForgeCommand(cmd) {
  const { text, bodies } = stripHeredocs(cmd);
  const repoCtx = repoContextOf(text);
  const ops = [];
  for (const stmt of splitStatements(text)) {
    if (ops.length >= OPS_PER_COMMAND_CAP) break;
    // Triggers match against the quote-blanked view; arguments (URLs, bodies,
    // api paths) extract from the original statement.
    const bare = unquoted(stmt);
    for (const parsed of [
      ...curlOps(stmt, bare, bodies, repoCtx),
      ...ghOps(stmt, bare, bodies, repoCtx),
      ...scriptOps(stmt, bare, bodies, repoCtx),
      ...teaOps(stmt, bare, bodies, repoCtx),
      ...gitPushOps(bare),
      ...pythonApiOps(stmt, bare, bodies, repoCtx),
    ]) {
      if (ops.length >= OPS_PER_COMMAND_CAP) break;
      ops.push(parsed);
    }
  }
  // A loop re-curling the same endpoint is one intent — collapse identical
  // coordinates, keeping the first (its content/fields are the same).
  const seen = new Set();
  return ops.filter((op) => {
    const k = `${op.channel}|${op.action}|${op.repo || ""}|${op.res}|${op.num || ""}|${(op.content || "").slice(0, 40)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// ── Result pairing + gleaning ────────────────────────────────────────────────

function contentText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n");
  }
  return "";
}

// Same contract as the sibling libs: is_error normalized to isError, plus the
// in-content failure marker Claude Code uses.
function resultErrored(result) {
  if (!result) return false;
  if (result.isError) return true;
  return contentText(result.content).includes("<tool_use_error>");
}

// Glean `#N → title` labels from a shell result. Two observed shapes: raw
// forge JSON (number+title fields) and the `PR #123: title` lines agents print
// via jq/--jq formatting. Labels only, never attribution.
export function gleanTitles(text, titles) {
  if (!text) return;
  const head = text.slice(0, 20000);
  if (head[0] === "{" || head[0] === "[") {
    try {
      const d = JSON.parse(text);
      const take = (o) => {
        const n = typeof o?.number === "number" ? o.number : null;
        const t = str(o?.title);
        if (n && t && !(n in titles)) titles[n] = t.slice(0, 200);
      };
      if (Array.isArray(d)) d.slice(0, GLEAN_CAP).forEach((o) => take(o));
      else if (d && typeof d === "object") {
        take(d);
        for (const v of Object.values(d)) {
          if (Array.isArray(v)) v.slice(0, GLEAN_CAP).forEach((o) => take(o));
        }
      }
      return;
    } catch { /* not JSON — fall through to line patterns */ }
  }
  let n = 0;
  const lineRe = /(?:PR|pull request|issue)\s*#(\d{1,7})[:\s—-]+["“]?([^\n"”]{4,160})/gi;
  let m;
  while ((m = lineRe.exec(head)) && n < GLEAN_CAP) {
    const num = Number(m[1]);
    if (!(num in titles)) { titles[num] = m[2].trim(); n += 1; }
  }
}

// A create's number only exists in its RESULT — a `…/pulls/612` URL or a
// "Created … #612" line.
function createdNumber(text) {
  const u = /\/(?:pulls|issues)\/(\d{1,7})\b/.exec(text || "");
  if (u) return Number(u[1]);
  const c = /creat\w*[^\n]{0,60}?#(\d{1,7})\b/i.exec(text || "");
  return c ? Number(c[1]) : null;
}

// ── Read-side observation capture ────────────────────────────────────────────
// The reads are where "the world the agent sees" lives: a GET pr result is the
// full issue JSON (description, state, labels), a comments/reviews GET is the
// thread, a pulls/N.diff response is the change itself, a run-log read is the
// CI failure being debugged. One bash result covers every statement in its
// command, so attachment is deliberately conservative: JSON carrying a
// `number` pins to the matching target; otherwise content only attaches when
// exactly one numbered target could own it.

const COMMENT_LIST_CAP = 12;
const DIFF_CAP = 30_000;
const LOG_TAIL_LINES = 40;

function commentAuthor(c) {
  const u = c?.user ?? c?.author;
  if (typeof u === "string") return u;
  if (u && typeof u === "object") return str(u.login) || str(u.username) || str(u.full_name) || str(u.name) || null;
  return null;
}

// The status slice a read OBSERVED, from full-target JSON — the forge analog
// of traceLinearOps.readFields. Field shapes differ by producer: the raw API
// nests base/head as {ref} objects and labels as {name} objects, while
// forgejo-pr.py view flattens both to plain strings — accept either, same as
// commentAuthor.
function obsFields(d) {
  const out = {};
  const refOf = (v) => (typeof v === "string" ? v : str(v?.ref));
  if (str(d?.state)) out.state = d.state;
  if (typeof d?.mergeable === "boolean") out.mergeable = String(d.mergeable);
  if (d?.merged === true) out.merged = "true";
  if (d?.draft === true) out.draft = "true";
  if (refOf(d?.base)) out.base = refOf(d.base);
  if (refOf(d?.head)) out.head = refOf(d.head);
  if (typeof d?.assignee === "string" ? d.assignee : str(d?.assignee?.login)) {
    out.assignee = typeof d.assignee === "string" ? d.assignee : d.assignee.login;
  }
  if (Array.isArray(d?.labels) && d.labels.length) {
    const names = d.labels.map((l) => (typeof l === "string" ? l : str(l?.name))).filter(Boolean);
    if (names.length) out.labels = names.slice(0, 8).join(", ");
  }
  return out;
}

// A comment/review thread out of a comments-shaped JSON result.
function commentsOf(json) {
  const arr = Array.isArray(json) ? json : Array.isArray(json?.comments) ? json.comments : null;
  if (!arr) return null;
  const out = arr.slice(0, COMMENT_LIST_CAP)
    .map((c) => ({ author: commentAuthor(c), body: cap(str(c?.body) || ""), state: str(c?.state) }))
    .filter((c) => c.body);
  return out.length ? out : null;
}

function looksLikeDiff(text) {
  return /^diff --git |\ndiff --git /.test((text || "").slice(0, 4000));
}

function attachObservations(drafts, text) {
  if (!text) return;
  const numbered = drafts.filter((d) => d.num && (d.res === "pr" || d.res === "issue"));
  const sole = numbered.length === 1 ? numbered[0] : null;
  let json = null;
  const head = text.trimStart();
  if (head[0] === "{" || head[0] === "[") {
    try { json = JSON.parse(text); } catch { /* jq-formatted text — no JSON attach */ }
  }

  if (json && !Array.isArray(json) && typeof json === "object") {
    const target = (typeof json.number === "number" && numbered.find((d) => d.num === json.number)) || sole;
    if (target && target.kind === "read") {
      const body = str(json.body);
      if (body) {
        const c = capInfo(body);
        target.observedBody = c.text;
        target.observedBodyTruncated = c.truncated;
      }
      target.fields = { ...obsFields(json), ...target.fields };
    }
  }
  const comments = json ? commentsOf(json) : null;
  if (comments) {
    const target = numbered.find((d) => /comment|review/.test(d.action) || /\/(comments|reviews)\b/.test(d.endpoint || "")) || sole;
    if (target && target.kind === "read") target.comments = comments;
  }
  if (looksLikeDiff(text)) {
    const target = numbered.find((d) => /diff/.test(d.action) || /\.(diff|patch)\b/.test(d.endpoint || ""))
      || (sole && sole.res === "pr" ? sole : null);
    if (target) {
      target.diff = text.length > DIFF_CAP ? text.slice(0, DIFF_CAP) : text;
      target.diffTruncated = text.length > DIFF_CAP;
    }
  }
  const logOwner = drafts.find((d) => d.res === "ci" && (/\blog/.test(d.action) || /\/logs\b/.test(d.endpoint || "")));
  if (logOwner && !json) {
    const lines = text.split(/\r?\n/);
    logOwner.log = lines.slice(-LOG_TAIL_LINES).join("\n");
    logOwner.logLines = lines.length;
    return;
  }
  // Fallback: most reads pipe through jq/--jq, so the result is FORMATTED
  // text, not JSON — but that text is exactly what entered the agent's
  // context. Keep it (capped) when one numbered target owns the whole result
  // and nothing structured attached.
  if (sole && sole.kind === "read" && !json && !sole.observedBody && !sole.comments && !sole.diff) {
    const c = capInfo(text.trim());
    if (c.text) {
      sole.observedText = c.text;
      sole.observedTextTruncated = c.truncated;
    }
  }
}

// One prompt's turns → its forge ops. Op shape mirrors traceLinearOps:
//   { key, channel, action, kind: read|write|browse|push, res, num, repo,
//     create, title, content, contentTruncated, fields, ok, titles,
//     observedBody?, comments?, diff?, log? }
// `titles` is a num→title map gleaned from the op's RESULT (labels only); the
// observation fields carry what the read actually SHOWED the agent.
// ── The MCP door ─────────────────────────────────────────────────────────────
// Forge servers exposed as MCP tools, called directly or through a code-mode
// cell's virtual blocks. Corpus-evidenced by the reading study's top brief
// (the single largest gap cluster): `mcp__forgejo_*__forgejo_api({endpoint,
// method})` plus named get_/list_ wrappers. Verbs outside the maps claim
// nothing — an unknown tool must not become a plausible-looking op.

const MCP_NOUN_RES = {
  pull_request: "pr", pull_requests: "pr", pull: "pr", pulls: "pr",
  issue: "issue", issues: "issue", comment: "issue", comments: "issue", issue_comment: "issue",
  commit: "commit", commits: "commit",
  actions_run: "ci", actions_runs: "ci", actions_tasks: "ci", workflow_run: "ci", workflow_runs: "ci",
  release: "release", releases: "release",
  branch: "branch", branches: "branch",
  repo: "repo", repos: "repo", repository: "repo",
};

function mcpForgeOps(name, input) {
  const action = String(name || "").split("__").pop();
  if (!/^forgejo_/.test(action)) return [];
  const verb = action.slice("forgejo_".length);
  const arg = input && typeof input === "object" ? input : {};
  const repo = [arg.owner, arg.repo].every((v) => typeof v === "string" && v)
    ? `${arg.owner}/${arg.repo}`
    : typeof arg.repository === "string" && arg.repository.includes("/") ? arg.repository : null;

  if (verb === "api") {
    const endpoint = str(arg.endpoint) || str(arg.path);
    const hit = endpoint ? parseForgePath(endpoint) : null;
    if (!hit) return [];
    const method = String(arg.method || "GET").toUpperCase();
    const write = method !== "GET" && method !== "HEAD";
    return [{
      channel: "mcp",
      action: `${method} ${hit.res}${hit.sub ? `/${hit.sub}` : ""}`,
      kind: write ? "write" : hit.num ? "read" : "browse",
      res: hit.res, num: hit.num, repo: hit.repo || repo,
      create: write && method === "POST" && !hit.num && (hit.res === "pr" || hit.res === "issue"),
      content: null, contentTruncated: false, fields: {}, endpoint: hit.endpoint,
    }];
  }

  const vm = /^(get|list|create|update|edit|delete|merge|close|reopen|add)_(.+)$/.exec(verb);
  const res = vm ? MCP_NOUN_RES[vm[2]] : null;
  if (!res) return [];
  const write = !/^(get|list)$/.test(vm[1]);
  const numRaw = arg.index ?? arg.number ?? arg.pull_number ?? arg.issue_number;
  const num = Number.isFinite(Number(numRaw)) && numRaw !== "" && numRaw != null ? Number(numRaw) : null;
  return [{
    channel: "mcp",
    action: `${vm[1]} ${res}`,
    kind: write ? "write" : num ? "read" : "browse",
    res, num, repo,
    create: vm[1] === "create" && !num,
    content: null, contentTruncated: false, fields: {}, endpoint: null,
  }];
}

export function extractForgeOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      const cmd = shellCommandOf(b);
      const drafts = cmd ? classifyForgeCommand(cmd) : mcpForgeOps(b.name, b.input);
      if (!drafts.length) continue;
      const result = b.id ? resultById.get(b.id) : null;
      const ok = !resultErrored(result);
      const text = result ? contentText(result.content) : "";
      const titles = {};
      if (ok && text) {
        gleanTitles(text, titles);
        attachObservations(drafts, text);
      }
      drafts.forEach((d, i) => {
        let num = d.num;
        if (d.create && !num && ok) num = createdNumber(text);
        const key = b.id ? `${b.id}#${i}` : `synth:${hashString(cmd || JSON.stringify(b.input ?? {}))}#${i}`;
        ops.push({
          ...d,
          num,
          key,
          title: d.title || (num && titles[num]) || null,
          ok,
          titles,
        });
      });
    }
  }
  return ops;
}

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

// Flatten per-prompt extractions into the window's real deltas — the same
// mechanic as the sibling collectWindow*Ops (oldest-first, first sighting per
// session wins) — plus one forge-specific pass: when the whole window names
// exactly ONE repo, repo-less ops (gh from a checkout's cwd, git push, $API
// bases) adopt it, flagged `repoAssumed` so the UI can hedge the label.
export function collectWindowForgeOps(promptEvents) {
  const sorted = [...(promptEvents || [])].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const seen = new Set();
  const flat = [];
  for (const p of sorted) {
    for (const op of p.ops || []) {
      const dedupKey = `${p.sessionId || ""}|${op.key}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      flat.push({
        ...op,
        promptId: p.promptId,
        sessionId: p.sessionId || "",
        endpointId: p.endpointId || "",
        endpointName: p.endpointName || "",
        ts: p.ts ?? null,
      });
    }
  }
  // Adoption is SESSION-scoped: a session that names exactly one repo labels
  // its own repo-less ops (gh from a checkout's cwd, git push). A session
  // naming none falls back to the window's sole repo when there is one — the
  // cross-session inference only fires when it cannot mislabel; a window
  // mixing repos never adopts across sessions.
  const windowRepos = new Set(flat.map((o) => o.repo).filter(Boolean));
  const windowSole = windowRepos.size === 1 ? [...windowRepos][0] : null;
  const bySession = new Map();
  for (const op of flat) {
    let ops = bySession.get(op.sessionId);
    if (!ops) { ops = []; bySession.set(op.sessionId, ops); }
    ops.push(op);
  }
  for (const ops of bySession.values()) {
    const named = new Set(ops.map((o) => o.repo).filter(Boolean));
    const sole = named.size === 1 ? [...named][0] : named.size === 0 ? windowSole : null;
    if (!sole) continue;
    for (const op of ops) {
      if (!op.repo) { op.repo = sole; op.repoAssumed = true; }
    }
  }
  return flat;
}

function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : "unknown";
}

function newActor(id, name) {
  return { id, name: name || shortId(id), reads: 0, writes: 0, pushes: 0, browses: 0, targetsRead: new Set(), targetsWritten: new Set(), sessions: new Set() };
}

// The window's forge activity — repo-, target-, and actor-centric:
//   repos:   [{ name (null = unresolved), assumed, targets:[target…],
//               stream:[op…] (repo-level: CI, commits, releases, pushes,
//               browses — chronological), reads, writes, pushes }]
//   targets: [{ id "#123", res: pr|issue, repo, title, reads, writes, created,
//               errored, readers, writers, sessions, channels, ops }]
//   actors:  [{ id, name, reads, writes, pushes, browses, targetsRead,
//               targetsWritten, sessions }] — an actor is the ENDPOINT, same
//               as the sibling views; friendly labels resolve in the component.
//   totals:  { targets, prs, issues, repos, reads, writes, created, pushes,
//              ci, browses, actors, channels: {curl, gh, script, tea, git} }
export function buildForgeActivity(flatOps) {
  const targets = new Map();
  const repoStreams = new Map();
  const actors = new Map();
  const titleOf = new Map();
  const channels = { curl: 0, gh: 0, script: 0, tea: 0, git: 0 };
  let reads = 0;
  let writes = 0;
  let browses = 0;
  let pushes = 0;
  let created = 0;
  let ci = 0;

  const repoKey = (op) => op.repo || "";
  const targetKey = (op) => `${repoKey(op)}!${op.res}!${op.num}`;

  for (const op of flatOps || []) {
    for (const [num, t] of Object.entries(op.titles || {})) {
      const k = `${repoKey(op)}!#${num}`;
      if (!titleOf.has(k)) titleOf.set(k, t);
    }
    if (op.num && op.title) {
      const k = `${repoKey(op)}!#${op.num}`;
      if (!titleOf.has(k)) titleOf.set(k, op.title);
    }
    if (op.channel in channels) channels[op.channel] += 1;

    let actor = actors.get(op.endpointId);
    if (!actor) {
      actor = newActor(op.endpointId, op.endpointName);
      actors.set(op.endpointId, actor);
    }
    actor.sessions.add(op.sessionId);

    let repoStream = repoStreams.get(repoKey(op));
    if (!repoStream) {
      repoStream = { name: op.repo || null, assumed: Boolean(op.repoAssumed), stream: [], reads: 0, writes: 0, pushes: 0 };
      repoStreams.set(repoKey(op), repoStream);
    }
    if (op.repo && !op.repoAssumed) repoStream.assumed = false;

    const opRecord = {
      promptId: op.promptId, sessionId: op.sessionId, endpointId: op.endpointId,
      endpointName: op.endpointName, ts: op.ts, channel: op.channel,
      action: op.action, kind: op.kind, res: op.res, create: op.create, ok: op.ok,
      content: op.content || null, contentTruncated: Boolean(op.contentTruncated),
      observedBody: op.observedBody || null, observedBodyTruncated: Boolean(op.observedBodyTruncated),
      observedText: op.observedText || null, observedTextTruncated: Boolean(op.observedTextTruncated),
      comments: op.comments || null,
      diff: op.diff || null, diffTruncated: Boolean(op.diffTruncated),
      log: op.log || null, logLines: op.logLines || 0,
      fields: op.fields || {}, endpoint: op.endpoint || null,
    };

    const isTarget = (op.res === "pr" || op.res === "issue") && op.num;
    if (!isTarget) {
      // Repo-level stream: CI, commits, releases, pushes, list-browses.
      repoStream.stream.push(opRecord);
      if (!op.ok) continue;
      if (op.res === "ci") ci += 1;
      if (op.kind === "push") { pushes += 1; repoStream.pushes += 1; actor.pushes += 1; }
      else if (op.kind === "write") { writes += 1; repoStream.writes += 1; actor.writes += 1; }
      else { browses += 1; actor.browses += 1; }
      continue;
    }

    let target = targets.get(targetKey(op));
    if (!target) {
      target = {
        id: `#${op.num}`, num: op.num, res: op.res, repo: op.repo || null, repoAssumed: Boolean(op.repoAssumed),
        reads: 0, writes: 0, created: 0, errored: 0,
        readers: new Map(), writers: new Map(), sessions: new Set(), channels: new Set(), ops: [],
      };
      targets.set(targetKey(op), target);
    }
    target.ops.push(opRecord);
    target.channels.add(op.channel);
    if (!op.ok) { target.errored += 1; continue; }
    target.sessions.add(op.sessionId);
    const side = op.kind === "write" ? target.writers : target.readers;
    const prev = side.get(op.endpointId) || { id: op.endpointId, name: op.endpointName || shortId(op.endpointId), n: 0 };
    prev.n += 1;
    side.set(op.endpointId, prev);
    if (op.kind === "write") {
      target.writes += 1;
      writes += 1;
      actor.writes += 1;
      actor.targetsWritten.add(targetKey(op));
      if (op.create) { target.created += 1; created += 1; }
    } else {
      target.reads += 1;
      reads += 1;
      actor.reads += 1;
      actor.targetsRead.add(targetKey(op));
    }
  }

  const targetList = [...targets.entries()].map(([k, t]) => ({
    ...t,
    title: titleOf.get(`${t.repo || ""}!#${t.num}`) || null,
    readers: [...t.readers.values()].sort((a, b) => b.n - a.n),
    writers: [...t.writers.values()].sort((a, b) => b.n - a.n),
    sessions: t.sessions.size,
    channels: [...t.channels],
    key: k,
    ops: [...t.ops].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0)),
  }));
  // Most-mutated first — same rationale as the Linear tab.
  targetList.sort((a, b) => (b.writes - a.writes) || (b.reads + b.writes) - (a.reads + a.writes) || a.num - b.num);

  const repoList = [...repoStreams.entries()].map(([k, r]) => {
    const repoTargets = targetList.filter((t) => (t.repo || "") === k);
    return {
      ...r,
      targets: repoTargets,
      stream: [...r.stream].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0)),
      reads: r.reads + repoTargets.reduce((s, t) => s + t.reads, 0),
      writes: r.writes + repoTargets.reduce((s, t) => s + t.writes, 0),
    };
  }).filter((r) => r.targets.length || r.stream.length);
  repoList.sort((a, b) => {
    if (!a.name !== !b.name) return a.name ? -1 : 1; // named repos first
    return (b.writes - a.writes) || (b.reads - a.reads) || String(a.name).localeCompare(String(b.name));
  });

  const actorList = [...actors.values()]
    .filter((a) => a.reads + a.writes + a.pushes + a.browses > 0)
    .map((a) => ({
      id: a.id, name: a.name,
      reads: a.reads, writes: a.writes, pushes: a.pushes, browses: a.browses,
      targetsRead: a.targetsRead.size, targetsWritten: a.targetsWritten.size,
      sessions: a.sessions.size,
    }));
  actorList.sort((a, b) => (b.writes - a.writes) || (b.reads - a.reads) || a.name.localeCompare(b.name));

  return {
    repos: repoList,
    targets: targetList,
    actors: actorList,
    totals: {
      targets: targetList.length,
      prs: targetList.filter((t) => t.res === "pr").length,
      issues: targetList.filter((t) => t.res === "issue").length,
      repos: repoList.filter((r) => r.name).length,
      reads, writes, created, pushes, ci, browses,
      actors: actorList.length,
      channels,
    },
  };
}

// ── Documents for the Files-style renderer ───────────────────────────────────
// Same mapping as the Linear tab: a target's DESCRIPTION behaves like a small
// file (create/PATCH bodies are whole-body writes, GET observedBody is a read
// observation), so reconstructFile replays it with per-line provenance — and
// a read disagreeing with the replayed state is real drift: the PR was edited
// off-window (web UI, another machine) between agent touches.

function isCommentWrite(op) {
  return /comment/.test(op.action) || /\/(comments|reviews)\b/.test(op.endpoint || "");
}

export function targetDescDoc(targetOps) {
  const ops = [];
  for (const op of targetOps || []) {
    if (op.kind === "write" && op.content && !isCommentWrite(op)) {
      ops.push({
        kind: "write", ok: op.ok, content: op.content,
        contentTruncated: Boolean(op.contentTruncated),
        promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
      });
    } else if (op.kind === "read" && op.observedBody) {
      const lines = op.observedBody.split("\n");
      ops.push({
        kind: "read", ok: op.ok,
        obs: lines.map((t, i) => [i + 1, t]),
        // A capped body can't pin the document's length — same contract as a
        // truncated file read.
        obsFull: !op.observedBodyTruncated,
        readLines: lines.length,
        promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
      });
    }
  }
  return ops;
}

// Comments as one-version mini-documents: a posted comment is a whole-body
// WRITE, one surfaced by a comments/reviews GET is a READ observation. Deduped
// by body — written-in-window provenance wins (same contract as
// traceLinearOps.issueCommentDocs).
export function targetCommentDocs(targetOps) {
  const byBody = new Map();
  for (const op of targetOps || []) {
    if (op.kind === "write" && op.content && isCommentWrite(op)) {
      const key = op.content.trim();
      if (!key) continue;
      byBody.set(key, {
        author: null,
        written: true,
        channel: op.channel,
        op: {
          kind: "write", ok: op.ok, content: op.content,
          contentTruncated: Boolean(op.contentTruncated),
          promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
        },
      });
    } else if (op.comments) {
      for (const c of op.comments) {
        const key = String(c.body || "").trim();
        if (!key || byBody.has(key)) continue;
        const lines = c.body.split("\n");
        byBody.set(key, {
          author: c.author || null,
          state: c.state || null,
          written: false,
          channel: op.channel,
          op: {
            kind: "read", ok: op.ok,
            obs: lines.map((t, i) => [i + 1, t]),
            obsFull: true, readLines: lines.length,
            promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
          },
        });
      }
    }
  }
  return [...byBody.values()].sort((a, b) => (tsOf(a.op.ts) ?? 0) - (tsOf(b.op.ts) ?? 0));
}

// Observed diffs, one read-only document each (deduped — a re-fetched
// identical diff collapses onto its first sighting).
export function targetDiffDocs(targetOps) {
  const seen = new Set();
  const out = [];
  for (const op of targetOps || []) {
    if (!op.diff) continue;
    const key = hashString(op.diff.slice(0, 2000));
    if (seen.has(key)) continue;
    seen.add(key);
    const lines = op.diff.split("\n");
    out.push({
      channel: op.channel,
      lines: lines.length,
      truncated: Boolean(op.diffTruncated),
      op: {
        kind: "read", ok: op.ok,
        obs: lines.map((t, i) => [i + 1, t]),
        obsFull: !op.diffTruncated,
        readLines: lines.length,
        promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
      },
    });
  }
  return out.sort((a, b) => (tsOf(a.op.ts) ?? 0) - (tsOf(b.op.ts) ?? 0));
}

// A formatted (jq-shaped) read result as a read observation — the raw text
// the agent's context actually received.
export function opObservedDoc(op) {
  if (!op?.observedText) return null;
  const lines = op.observedText.split("\n");
  return {
    kind: "read", ok: op.ok,
    obs: lines.map((t, i) => [i + 1, t]),
    obsFull: !op.observedTextTruncated,
    readLines: lines.length,
    promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
  };
}

// A CI log tail as a read observation. The tail's absolute line anchors start
// where the kept lines start, so the renderer's leading gap band says exactly
// what happened: "lines 1–N not observed".
export function logObsDoc(op) {
  if (!op?.log) return null;
  const lines = op.log.split("\n");
  const start = Math.max(1, (op.logLines || lines.length) - lines.length + 1);
  return {
    kind: "read", ok: op.ok,
    obs: lines.map((t, i) => [start + i, t]),
    obsFull: start === 1,
    readLines: op.logLines || lines.length,
    promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
  };
}
